/**
 * @packageDocumentation
 * @module f1/fn
 */

import is_function from '../is/is_function';

/**
 *
 * @param ignore the value does not affect the final outcome
 */
export type MethodValue<V, W> = (value: V) => V | W;

export interface Method {
  /**
   * Invoke a method on an object if it exists on the object, otherwise
   * return the value
   *
   * @public
   * @function f1.method
   * @param obj the object which has the method
   * @param method the name of the method on the object
   * @param value the value to pass to the method
   * @example
   *
   * ```ts
   * const method = require('1-liners/method');
   *
   * 	const object = {
   * 		base: 1,
   * 		add(number) { return this.base + number; },
   * 	};
   *
   * 	method('add', object)(5) => 6
   * ```
   */
  <O extends Record<string, (value: V) => W>, V, W>(obj: O, method: string, value: V): V | W;

  /**
   * @param obj the object which has the method
   * @param method the name of the method on the object
   */
  <O extends Record<string, (value: V) => W>, V, W>(obj: O, method: string): MethodValue<V, W>;
}

/**
 * @see Method
 */
export default <O extends Record<string, (value: V) => W>, V, W>(obj: O, method: string, value: V): V | W =>
  is_function(obj[method]) ? obj[method](value) : value;
